/**
 * Camera audio (listen-in) toggle on Edit Camera — redesign preview (ENG-2204).
 *
 * A camera's mic reaches live view only when actuate_admin's per-camera
 * `allowed_media_types` is "video+audio" (the monitoring-api mint derives
 * `has_audio` from it). Until now that was editable only in V1 Django admin,
 * and the change only reaches the connector after a site Sync + connector
 * restart, which nothing told anyone. This card owns the field: it saves on
 * its own (PATCH general_info with just this field), marks the site unsynced
 * so the existing Sync control turns red, and says how to apply it.
 *
 * Backend dependency: `allowed_media_types` is not in admin's
 * CameraGeneralInfoSerializer yet. While the general_info payload lacks the
 * key, the switch renders disabled with "Not editable yet"; once admin adds it
 * the card becomes editable with no camera-ui change.
 *
 * Gate: LaunchDarkly `showListenIn` AND staff AND an RTSP-pull site.
 * Listen-in is planned as a paid per-camera add-on; billing isn't built.
 * TODO(ENG-2204): also allow a `listen_in` permission (login-time user/info)
 * once the add-on exists, instead of staff only.
 */
import React from "react";
import Switch from "@mui/material/Switch";
import { create } from "zustand";
import { createApi } from "../../utils/createApi";
import { markSiteUnsynced } from "../../utils/syncStore";
import { useIsStaffUser } from "../../utils/staffUser";
import { useListenInEnabled } from "../Alerts/ListenInButton";
import { talkDownSupportsSite } from "../../common/TalkDown/useTalkDownEnabled";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";

export const CAMERA_AUDIO_FIELD = "allowed_media_types";
export const CAMERA_AUDIO_ON_VALUE = "video+audio";
export const CAMERA_AUDIO_OFF_VALUE = "video";

export const CAMERA_AUDIO_LABEL = "Camera audio (listen-in)";
export const CAMERA_AUDIO_HELPER_TEXT =
  "Lets staff and operators hear this camera's microphone in live view. Some recorders stop sending video when audio is requested; if video drops after turning this on, turn it off.";
export const CAMERA_AUDIO_SAVED_NOTE =
  "Saved. Sync the site to apply. If the site is armed, accept the restart when you sync — re-arming alone won't apply it.";
export const CAMERA_AUDIO_NOT_EDITABLE = "Not editable yet";
export const CAMERA_AUDIO_NOT_EDITABLE_DETAIL =
  "Camera Admin doesn't expose this setting to the app yet. Change it in V1 admin for now.";
export const CAMERA_AUDIO_SAVE_FAILED =
  "Couldn't save camera audio. Try again.";

/** "video+audio" (any value naming audio) = on; blank/"video" = off. */
export const cameraAudioIsOn = (value: unknown): boolean =>
  typeof value === "string" && value.toLowerCase().includes("audio");

export const cameraAudioValue = (on: boolean): string =>
  on ? CAMERA_AUDIO_ON_VALUE : CAMERA_AUDIO_OFF_VALUE;

/**
 * The General Info tab is re-rendered from many places with whichever
 * cameraGeneralInfo snapshot that path captured, so a value saved here would
 * read stale after a tab switch. Saved values live here, keyed by camera id,
 * and win over the prop until the page reloads general_info.
 */
interface SavedCameraAudioState {
  savedByCamera: Record<string, string>;
  setSaved: (cameraId: string, value: string) => void;
}

export const useSavedCameraAudioStore = create<SavedCameraAudioState>(
  (set) => ({
    savedByCamera: {},
    setSaved: (cameraId, value) =>
      set((state) => ({
        savedByCamera: { ...state.savedByCamera, [cameraId]: value },
      })),
  })
);

/** Flag + staff + RTSP-pull site. Unknown site type = hidden. */
export const useCameraAudioSettingVisible = (
  integrationTypeName: string | null | undefined
): boolean => {
  const flagOn = useListenInEnabled();
  // TODO(ENG-2204): `|| userPermissions.listen_in` once the paid add-on ships.
  const staff = useIsStaffUser();
  return flagOn && staff && talkDownSupportsSite(integrationTypeName);
};

interface CameraAudioSettingProps {
  cameraGeneralInfo: Record<string, any> | null | undefined;
  /** Defaults to the Edit Camera store's site. */
  integrationTypeName?: string | null;
}

export const CameraAudioSetting: React.FC<CameraAudioSettingProps> = ({
  cameraGeneralInfo,
  integrationTypeName,
}) => {
  const storeIntegrationType = useEditCameraStore(
    (state) => state.siteInfoEditCamera?.integration_type_name
  );
  const siteType =
    integrationTypeName !== undefined
      ? integrationTypeName
      : storeIntegrationType;
  const visible = useCameraAudioSettingVisible(siteType);

  const cameraId =
    cameraGeneralInfo?.id != null ? String(cameraGeneralInfo.id) : null;
  const editable =
    !!cameraGeneralInfo &&
    Object.prototype.hasOwnProperty.call(cameraGeneralInfo, CAMERA_AUDIO_FIELD);
  const savedValue = useSavedCameraAudioStore((state) =>
    cameraId ? state.savedByCamera[cameraId] : undefined
  );
  const setSaved = useSavedCameraAudioStore((state) => state.setSaved);
  const currentOn = cameraAudioIsOn(
    savedValue !== undefined
      ? savedValue
      : cameraGeneralInfo?.[CAMERA_AUDIO_FIELD]
  );

  const [draftOn, setDraftOn] = React.useState(currentOn);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSavedNote] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setDraftOn(currentOn);
  }, [currentOn, cameraId]);

  if (!visible || !cameraId) return null;

  const dirty = editable && draftOn !== currentOn;

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    setSavedNote(false);
    const next = cameraAudioValue(draftOn);
    try {
      const response = await createApi("").patch(
        `/camera/${cameraId}/general_info/`,
        { [CAMERA_AUDIO_FIELD]: next }
      );
      const confirmed = response?.data?.[CAMERA_AUDIO_FIELD];
      setSaved(cameraId, typeof confirmed === "string" ? confirmed : next);
      // allowed_media_types rides the connector settings file: needs a Sync
      // (and, on an armed site, the restart the sync prompt offers).
      markSiteUnsynced();
      setSavedNote(true);
    } catch {
      setError(CAMERA_AUDIO_SAVE_FAILED);
    } finally {
      setSaving(false);
    }
  };

  const switchId = `camera-audio-switch-${cameraId}`;

  return (
    <div
      className="m-4 rounded-lg border border-[#E2E8F2] bg-white px-4 py-3 font-mulish text-actuate-blue"
      data-testid="camera-audio-setting"
    >
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor={switchId} className="text-[15px] font-bold">
          {CAMERA_AUDIO_LABEL}
        </label>
        <div className="flex items-center">
          <Switch
            id={switchId}
            checked={draftOn}
            color="warning"
            disabled={!editable || saving}
            inputProps={{ "aria-label": CAMERA_AUDIO_LABEL }}
            onChange={(event) => {
              const nextOn = event.target.checked;
              setDraftOn(nextOn);
              setSavedNote(false);
              setError(null);
            }}
          />
          <span className="text-[14px]">{draftOn ? "On" : "Off"}</span>
        </div>
        {!editable && (
          <span className="rounded-full bg-[#F5F7FA] px-2.5 py-0.5 text-[12px] font-semibold text-[#5B6B86]">
            {CAMERA_AUDIO_NOT_EDITABLE}
          </span>
        )}
        {dirty && (
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded bg-actuate-orange px-3 py-1 text-[13px] font-bold text-white hover:bg-[#e67700] disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        )}
      </div>
      <p className="mt-1 text-[13px] text-[#5B6B86]">
        {CAMERA_AUDIO_HELPER_TEXT}
      </p>
      {!editable && (
        <p className="mt-1 text-[12px] text-[#9AA3B5]">
          {CAMERA_AUDIO_NOT_EDITABLE_DETAIL}
        </p>
      )}
      {saved && (
        <p
          role="status"
          className="mt-2 rounded border-l-4 border-actuate-orange bg-[#FFF4E5] px-3 py-2 text-[13px]"
        >
          {CAMERA_AUDIO_SAVED_NOTE}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-[13px] text-[#D93636]">
          {error}
        </p>
      )}
    </div>
  );
};
